import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { QAToolsService } from '../../services/qatools.service';
import { forkJoin, Observable } from 'rxjs';
import { takeUntil, distinctUntilChanged, finalize } from 'rxjs/operators';
import { ClientSelectionService } from '../clients/client-selection.service';
import { Beneficiary, BeneficiaryManagementRequest, ClaimDetails, CreateDiariesRequest, FTPItem, RREItem, SimulateClaimSubmissionRequest } from '../../models/qatools.model';
import { formatHttpErrorMessage } from '../../shared/utils/error.utils';
import { resolveLastUpdatedBy } from '../../shared/utils/user.utils';
import { DestroyableComponent } from '../../shared/base/destroyable.component';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, Sort } from '@angular/material/sort';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { map, startWith } from 'rxjs';
import { PageEvent } from '@angular/material/paginator';
import { toggleAutocompletePanel } from '../../shared/utils/autocomplete-panel.utils';

interface QaGridRow {
  claimKey: string;
  claimantKey: string;
  insuranceType: string;
  medicareEligibilityStatus: string;
}

@Component({
  selector: 'app-qatools',
  standalone: false,
  templateUrl: './qatools.component.html',
  styleUrl: './qatools.component.css',
})
export class QatoolsComponent extends DestroyableComponent implements OnInit, AfterViewInit {

  clientId?: number;
  isLoading = false;
  ftpList: FTPItem[] = [];
  rreList: RREItem[] = [];
  claimDetailsList: ClaimDetails[] = [];
  selectedFTP?: FTPItem;
  selectedRRE?: RREItem;
  ftpControl = new FormControl<string | FTPItem>('');
  rreControl = new FormControl<string | RREItem>('');
  filteredFtpOptions$: Observable<FTPItem[]> = new Observable<FTPItem[]>();
  filteredRreOptions$: Observable<RREItem[]> = new Observable<RREItem[]>();
  selectedDate = this.formatDateForInput(new Date());
  maxDate = this.formatDateForInput(new Date());
  isMovingToProd = false;
  isCreatingDiaries = false;
  isMarkingBeneficiaries = false;
  isSubmittingClaims = false;
  successMessage?: string;
  dropdownChoices: string[] = ['Positive', 'Negative'];
  dispositionCodeOptions: string[] = ['01', '02', '03', 'SP','GL'];
  readonly displayedColumns: string[] = [
    'InjuredPartyName',
    'ClaimNumber',
    'InsuranceType',
    'ClaimKey',
    'DispositionCode',
    'ErrorCode',
    'Update',
  ];
  
  // Pagination & Sorting
  dataSource = new MatTableDataSource<ClaimDetails>([]);
  totalCount = 0;
  pageIndex = 0;
  pageSize = 10;
  sortBy = 'ClaimNumber';
  sortDir: 'asc' | 'desc' = 'asc';
  claimsPageSizeOptions = [10, 20, 50];
  errorMessage = '';
  
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('ftpAuto') ftpAutocomplete!: MatAutocomplete;

  gridRows: QaGridRow[] = [{
    claimKey: '',
    claimantKey: '',
    insuranceType: '',
    medicareEligibilityStatus: '',
  }];

  constructor(
    private qatoolsService: QAToolsService,
    private clientSelection: ClientSelectionService
  ){
    super();
  }

  ngOnInit(): void {
    this.initializeDropdownFilters();
    this.resolveClientDetails();
  }

  ngAfterViewInit(): void {
    this.bindSortEvents();
  }

  resolveClientDetails(): void {
    this.clientSelection.selectedClient$
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((client) => {
        const clientId = client?.ClientID;
        if(clientId != null) {
          this.clientId = clientId;
          this.selectedFTP = undefined;
          this.selectedRRE = undefined;
          this.ftpControl.setValue('', { emitEvent: false });
          this.rreControl.setValue('', { emitEvent: false });
          this.selectedDate = this.formatDateForInput(new Date());
          this.successMessage = undefined;
          this.loadQAData();
        } else {
          this.ftpList = [];
          this.rreList = [];
          this.claimDetailsList = [];
          this.dataSource.data = [];
          this.totalCount = 0;
          this.selectedFTP = undefined;
          this.selectedRRE = undefined;
          this.ftpControl.setValue('', { emitEvent: false });
          this.rreControl.setValue('', { emitEvent: false });
          this.selectedDate = '';
          this.successMessage = undefined;
          this.errorMessage = 'Please select a client to view QA Tools.';
        }
      })
  }

  loadQAData(skip = 0, count = 20): void {
    if(!this.clientId) return;

    this.isLoading = true;
    this.errorMessage = '';

    forkJoin({
      ftps: this.qatoolsService.getFTPs(this.clientId, skip, count),
      rres: this.qatoolsService.getRREs(),
    })
      .pipe(takeUntil(this.destroy$), finalize(() => this.isLoading = false))
      .subscribe({
        next: ({ ftps, rres }) => {
          this.ftpList = ftps.FTPs;
          this.rreList = rres.rres;
          this.successMessage = undefined;
          this.refreshDropdownOptionViews();

          if (this.ftpList.length === 0) {
            this.errorMessage = 'No FTPs found for this client.';
          }
        },
        error: (err) => {
          this.errorMessage = formatHttpErrorMessage('Failed to load QA tools data.', err);
          console.error('Error loading QA tools data:', err);
          this.ftpList = [];
          this.rreList = [];
        }
      });
  }

  private bindSortEvents(): void {
    if (!this.sort) return;
    
    this.sort.sortChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((sortEvent: Sort) => {
        this.sortBy = sortEvent.active || 'ClaimNumber';
        this.sortDir = (sortEvent.direction as 'asc' | 'desc') || 'asc';
        this.pageIndex = 0;
        this.loadClaims();
      });
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadClaims();
  }

  private loadClaims(): void {
    if (!this.clientId || !this.selectedRRE?.rre) {
      this.claimDetailsList = [];
      this.dataSource.data = [];
      this.totalCount = 0;
      return;
    }

    const skip = this.pageIndex * this.pageSize;
    const count = this.pageSize;

    this.qatoolsService.getClaimsByRRE(this.selectedRRE.rre, this.sortBy, this.sortDir, skip, count)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.claimDetailsList = (Array.isArray(response.Claims) ? response.Claims : []).map((claim) => ({
            ...claim,
            DispositionCode: claim.DispositionCode ?? '',
            ErrorCode: claim.ErrorCode ?? '',
            ShouldUpdate: Boolean(claim.ShouldUpdate),
          }));

          this.dataSource.data = this.claimDetailsList;
          this.totalCount = response.totalCount;

          if (this.claimDetailsList.length === 0) {
            this.errorMessage = 'No claims found for the selected RRE.';
          }
        },
        error: (err) => {
          console.error('Error fetching claims by RRE:', err);
          this.errorMessage = formatHttpErrorMessage('Failed to fetch claims for the selected RRE.', err);
          this.claimDetailsList = [];
          this.dataSource.data = [];
          this.totalCount = 0;
        }
      });
  }

  startCreateDiaries(): void {
    this.errorMessage = '';
    this.successMessage = undefined;

    if (!this.clientId) {
      this.errorMessage = 'Please select a client.';
      return;
    }

    if (!this.selectedFTP?.ftpDirectoryName) {
      this.errorMessage = 'Please select an FTP.';
      return;
    }

    if (this.selectedDate && this.isFutureDate(this.selectedDate)) {
      this.errorMessage = 'Future dates are not allowed.';
      return;
    }

    const request: CreateDiariesRequest = {
      ClientID: this.clientId,
      FtpServer: this.selectedFTP.ftpDirectoryName,
      DateFrom: this.selectedDate,
    };

    this.isCreatingDiaries = true;

    this.qatoolsService.createDiaries(request)
      .pipe(takeUntil(this.destroy$), finalize(() => this.isCreatingDiaries = false))
      .subscribe({
        next: () => {
          this.successMessage = 'Create diaries started successfully.';
        },
        error: (err) => {
          console.error('Error creating diaries:', err);
          this.errorMessage = formatHttpErrorMessage('Failed to start create diaries.', err);
        }
      });
  }

  runNightlyJob(): void {
    this.errorMessage = '';
    this.successMessage = undefined;

    this.isMovingToProd = true;

    this.qatoolsService.runNightlyJob()
      .pipe(takeUntil(this.destroy$), finalize(() => this.isMovingToProd = false))
      .subscribe({
        next: () => {
          this.successMessage = 'Move Stage to Prod started successfully.';
        },
        error: (err) => {
          console.error('Error moving Stage to Prod:', err);
          this.errorMessage = formatHttpErrorMessage('Failed to move Stage to Prod.', err);
        }
      });
  }

  getClaimsByRRE(): void {
    this.errorMessage = '';
    this.successMessage = undefined;
    
    this.claimDetailsList = [];
    this.dataSource.data = [];

    const selectedRreValue = this.selectedRRE?.rre ?? '';
    if (!selectedRreValue) {
      this.totalCount = 0;
      return;
    }

    this.pageIndex = 0;
    this.loadClaims();
  }

  onFtpInputChange(): void {
    if (typeof this.ftpControl.value === 'string') {
      this.selectedFTP = undefined;
    }
  }

  onRreInputChange(): void {
    if (typeof this.rreControl.value === 'string') {
      this.selectedRRE = undefined;
      this.claimDetailsList = [];
      this.dataSource.data = [];
      this.totalCount = 0;
    }
  }

  onFtpSelected(ftp: FTPItem): void {
    this.selectedFTP = ftp;
    this.ftpControl.setValue(ftp, { emitEvent: false });
  }

  onRreSelected(rre: RREItem): void {
    this.selectedRRE = rre;
    this.rreControl.setValue(rre, { emitEvent: false });
    this.getClaimsByRRE();
  }

  clearSelectedFtp(event: Event): void {
    event.stopPropagation();
    this.selectedFTP = undefined;
    this.ftpControl.setValue('');
    this.ftpAutocomplete?.options?.forEach(opt => opt.deselect());
  }

  clearSelectedRre(event: Event): void {
    event.stopPropagation();
    this.selectedRRE = undefined;
    this.rreControl.setValue('');
    this.getClaimsByRRE();
  }

  openOrClosePanel(event: Event, trigger: MatAutocompleteTrigger): void {
    toggleAutocompletePanel(event, trigger);
  }

  displayFtpText(ftp: FTPItem | string | null): string {
    if (!ftp) return '';
    if (typeof ftp === 'string') return ftp;
    return ftp.ftpDirectoryName ?? '';
  }

  displayRreText(rre: RREItem | string | null): string {
    if (!rre) return '';
    if (typeof rre === 'string') return rre;
    return `${rre.rre ?? ''}${rre.rreName ? ' - ' + rre.rreName : ''}`;
  }

  onClaimDispositionCodeChange(claim: ClaimDetails): void {
    if (claim.DispositionCode !== 'SP') {
      claim.ErrorCode = '';
    }
  }

  clearBeneficiaryStatus(event: Event, row: QaGridRow): void {
    event.stopPropagation();
    row.medicareEligibilityStatus = '';
  }

  clearDispositionCode(event: Event, claim: ClaimDetails): void {
    event.stopPropagation();
    claim.DispositionCode = '';
    this.onClaimDispositionCodeChange(claim);
  }

  filterSimpleOptions(options: string[], value: string): string[] {
    const filterValue = (value ?? '').trim().toLowerCase();

    if (!filterValue) {
      return options.slice();
    }

    return options.filter((option) => option.toLowerCase().includes(filterValue));
  }

  hasClaimUpdatesToSubmit(): boolean {
    return this.claimDetailsList.some((claim) => claim.ShouldUpdate);
  }

  simulateClaimSubmission(): void {
    this.errorMessage = '';
    this.successMessage = undefined;

    const selectedRreValue = this.selectedRRE?.rre ?? '';
    if (!selectedRreValue) {
      this.errorMessage = 'Please select an RRE.';
      return;
    }

    const selectedClaims = this.claimDetailsList.filter((claim) => claim.ShouldUpdate);
    if (selectedClaims.length === 0) {
      this.errorMessage = 'Select at least one claim to simulate submission/response.';
      return;
    }

    const hasInvalidClaims = selectedClaims.some((claim) =>
      !claim.DispositionCode || (claim.DispositionCode === 'SP' && !claim.ErrorCode.trim())
    );

    if (hasInvalidClaims) {
      this.errorMessage = 'Each selected claim requires a disposition code. Error code is required when disposition code is SP.';
      return;
    }

    const payload = selectedClaims.map((claim) => ({
      ...claim,
      DispositionCode: claim.DispositionCode,
      ErrorCode: claim.DispositionCode === 'SP' ? claim.ErrorCode.trim() : '',
    }));

    const request: SimulateClaimSubmissionRequest = {
      RRE: selectedRreValue,
      Claims: payload,
      UpdatedBy: resolveLastUpdatedBy(),
    };

    this.isSubmittingClaims = true;

    this.qatoolsService.simulateClaimSubmission(request)
      .pipe(takeUntil(this.destroy$), finalize(() => this.isSubmittingClaims = false))
      .subscribe({
        next: (message) => {
          this.successMessage = message || `${payload.length} claim(s) submitted and processed successfully.`;
          this.getClaimsByRRE();
        },
        error: (err) => {
          console.error('Error simulating claim submission:', err);
          this.errorMessage = formatHttpErrorMessage('Failed to simulate claim submission/response.', err);
        }
      });
  }

  markBeneficiaries(): void {
    this.errorMessage = '';
    this.successMessage = undefined;

    const normalizedRows = this.gridRows.map((row) => ({
      claimKey: row.claimKey.trim(),
      claimantKey: row.claimantKey.trim(),
      insuranceType: row.insuranceType.trim(),
      medicareEligibilityStatus: row.medicareEligibilityStatus.trim(),
    }));

    const hasPartialRows = normalizedRows.some((row) => {
      const hasAnyValue =
        row.claimKey !== '' ||
        row.claimantKey !== '' ||
        row.insuranceType !== '' ||
        row.medicareEligibilityStatus !== '';

      const hasAllValues =
        row.claimKey !== '' &&
        row.claimantKey !== '' &&
        row.insuranceType !== '' &&
        row.medicareEligibilityStatus !== '';

      return hasAnyValue && !hasAllValues;
    });

    if (hasPartialRows) {
      this.errorMessage = 'If a row is used, all columns in that row are required.';
      return;
    }

    const beneficiaries: Beneficiary[] = normalizedRows
      .filter((row) =>
        row.claimKey !== '' &&
        row.claimantKey !== '' &&
        row.insuranceType !== '' &&
        row.medicareEligibilityStatus !== ''
      )
      .map((row) => ({
        ClaimKey: row.claimKey,
        ClaimantKey: row.claimantKey,
        InsuranceType: row.insuranceType,
        MedicareEligibilityStatus: this.toMedicareEligibilityCode(row.medicareEligibilityStatus),
      }));

    if (beneficiaries.length === 0) {
      this.errorMessage = 'Please fill all columns in at least one row.';
      return;
    }

    const request: BeneficiaryManagementRequest = {
      Beneficiaries: beneficiaries,
      UpdatedBy: resolveLastUpdatedBy()
    };

    this.isMarkingBeneficiaries = true;

    this.qatoolsService.markBeneficiaries(request)
      .pipe(takeUntil(this.destroy$), finalize(() => this.isMarkingBeneficiaries = false))
      .subscribe({
        next: () => {
          this.successMessage = 'Beneficiaries marked successfully.';
        },
        error: (err) => {
          console.error('Error marking beneficiaries:', err);
          this.errorMessage = formatHttpErrorMessage('Failed to mark beneficiaries.', err);
        },
      });
  }

  private isFutureDate(value: string): boolean {
    const selected = new Date(`${value}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selected > today;
  }

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  addBeneficiaryRow(): void {
    if (this.gridRows.length >= 5) return;
    this.gridRows.push({
      claimKey: '',
      claimantKey: '',
      insuranceType: '',
      medicareEligibilityStatus: '',
    });
  }

  removeBeneficiaryRow(index: number): void {
    if (this.gridRows.length > 1) {
      this.gridRows.splice(index, 1);
    }
  }

  trackByStringFTP(index: number, item: FTPItem): string {
    return item?.ftpDirectoryName || '';
  }

  trackByStringRRE(index: number, item: RREItem): string {
    return item?.rreName || '';
  }

  private toMedicareEligibilityCode(value: string): string {
    const normalized = value.trim().toLowerCase();

    if (normalized === 'positive') {
      return '01';
    }

    if (normalized === 'negative') {
      return '51';
    }

    return value.trim();
  }

  hasAtLeastOneCompleteRow(): boolean {
    return this.gridRows.some((row) =>
      row.claimKey.trim() !== '' &&
      row.claimantKey.trim() !== '' &&
      row.insuranceType.trim() !== '' &&
      row.medicareEligibilityStatus.trim() !== ''
    );
  }

  private initializeDropdownFilters(): void {
    this.filteredFtpOptions$ = this.ftpControl.valueChanges.pipe(
      startWith(this.ftpControl.value),
      map((value) => this.filterFtpOptions(value))
    );

    this.filteredRreOptions$ = this.rreControl.valueChanges.pipe(
      startWith(this.rreControl.value),
      map((value) => this.filterRreOptions(value))
    );
  }

  private refreshDropdownOptionViews(): void {
    this.ftpControl.setValue(this.selectedFTP ?? this.ftpControl.value ?? '', { emitEvent: true });
    this.rreControl.setValue(this.selectedRRE ?? this.rreControl.value ?? '', { emitEvent: true });
  }

  private filterFtpOptions(value: string | FTPItem | null): FTPItem[] {
    const filterValue = this.normalizeAutocompleteValue(value, (item) => item.ftpDirectoryName ?? '');

    if (!filterValue) {
      return this.ftpList.slice();
    }

    return this.ftpList.filter((ftp) =>
      (ftp.ftpDirectoryName ?? '').toLowerCase().includes(filterValue)
    );
  }

  private filterRreOptions(value: string | RREItem | null): RREItem[] {
    const filterValue = this.normalizeAutocompleteValue(value, (item) => `${item.rre ?? ''} ${item.rreName ?? ''}`);

    if (!filterValue) {
      return this.rreList.slice();
    }

    return this.rreList.filter((rre) =>
      `${rre.rre ?? ''} ${rre.rreName ?? ''}`.toLowerCase().includes(filterValue)
    );
  }

  private normalizeAutocompleteValue<T>(value: string | T | null, getText: (option: T) => string): string {
    if (!value) {
      return '';
    }

    return (typeof value === 'string' ? value : getText(value)).trim().toLowerCase();
  }
}
